import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { getApiHeaders } from "../utils/helpers";
import { BASE_URL } from "../api";
import MessageToster from "../component/MessageToster";
import Topbar from "../component/Topbar";
import Sidebar from "../component/Sidebar";
import Footer from "../component/Footer";
import Loader from "../component/Loader";
import TwoFactorModal from "../component/TwoFactorModal";
import ChangePassword from "./ChangePassword";
import DeviceManagement from "./DeviceManagement";
import { useHandleError } from "../hooks/useHandleError";
import { useUser } from "../context/UserContext";

export default function Security() {
    const handleError = useHandleError();
    const { userData } = useUser() || {};
    const [loading, setLoading] = useState(false);

    const [twoFaStatus, setTwoFaStatus] = useState(0);
    const [twoFaData, setTwoFaData] = useState(null);
    const [twoFaLoading, setTwoFaLoading] = useState(false);
    const [disableOtp, setDisableOtp] = useState(["", "", "", "", "", ""]);
    const disableOtpRefs = useRef([]);
    const [loginId, setLoginId] = useState("");

    const getProfileData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${BASE_URL}/account/get_user_data`, { headers: getApiHeaders() });

            if (res.data.code === 200) {
                const data = res.data.data;
                const status = (data.is_2fa_enabled || data.two_fa_status || data.is_2fa || data.two_factor_status) ? 1 : 0;
                setTwoFaStatus(status);
                if (data?.loginid) {
                    setLoginId(data.loginid);
                }
            }
        } catch (err) {
            handleError(err);
        } finally {
            setLoading(false);
        }
    }, [handleError]);

    useEffect(() => {
        getProfileData();
    }, [getProfileData]);

    const handleDisableOtpInput = (e, idx) => {
        const val = e.target.value.replace(/\D/g, "").slice(-1);
        const newOtp = [...disableOtp];
        newOtp[idx] = val;
        setDisableOtp(newOtp);
        if (val && idx < 5) {
            disableOtpRefs.current[idx + 1]?.focus();
        }
    };

    const handleDisableOtpKeyDown = (e, idx) => {
        if (e.key === "Backspace" && !disableOtp[idx] && idx > 0) {
            disableOtpRefs.current[idx - 1]?.focus();
        }
    };

    const handleDisableOtpPaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
        if (pasteData) {
            const newOtp = [...disableOtp];
            for (let i = 0; i < pasteData.length; i++) {
                if (i < 6) newOtp[i] = pasteData[i];
            }
            setDisableOtp(newOtp);
            const nextEmpty = newOtp.findIndex((v) => !v);
            disableOtpRefs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus();
        }
    };

    const handle2FASetup = async () => {
        setTwoFaLoading(true);
        try {
            const res = await axios.get(
                `${BASE_URL}/account/generate_private_key_2fa`,
                { headers: getApiHeaders() }
            );

            if (res.data.code === 200) {
                setTwoFaData(res.data);

                const modalEl = document.getElementById("twoFactorModal");
                if (modalEl && window.bootstrap?.Modal) {
                    const bsModal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
                    bsModal.show();
                }
            } else {
                MessageToster("error", res.data.message || "Failed to generate 2FA key.");
            }
        } catch (err) {
            MessageToster("error", err.message || "Something went wrong.");
        } finally {
            setTwoFaLoading(false);
        }
    };

    const confirmDisable2FA = async () => {
        const code = disableOtp.join("");
        if (code.length < 6) {
            MessageToster("error", "Please enter a valid 6-digit OTP.");
            return;
        }

        setTwoFaLoading(true);
        try {
            const res = await axios.post(
                `${BASE_URL}/account/verify_otp_2fa`,
                {
                    enable: false,
                    otp: code,
                },
                { headers: getApiHeaders() }
            );

            if (res.data.code === 200) {
                MessageToster("success", res.data.message || "2FA has been disabled.");
                getProfileData();

                const modalEl = document.getElementById("disable2FaModal");
                if (modalEl && window.bootstrap?.Modal) {
                    window.bootstrap.Modal.getOrCreateInstance(modalEl).hide();
                }
                setDisableOtp(["", "", "", "", "", ""]);
            } else {
                MessageToster("error", res.data.message || "Failed to disable 2FA.");
            }
        } catch (err) {
            MessageToster("error", err.message || "Something went wrong.");
        } finally {
            setTwoFaLoading(false);
        }
    };

    return (
        <>
            <div id="layout-wrapper">
                {loading && <Loader />}
                <Topbar />
                <Sidebar />
                <div className="main-content">
                    <div className="page-content">
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-12">
                                    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                        <h2 className="fw-700 mb-sm-0">Security</h2>
                                    </div>
                                </div>
                                <div className="col-12">

                                    {/* 2FA Block */}
                                    <div className="card mb-3">
                                        <div className="card-header bg-transparent border-bottom-0 pb-0 pt-4">
                                            <h5 className="mb-0 fw-bold">Two-Factor Authentication :</h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="bg-light rounded p-3 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                                        <i className="mdi mdi-shield-check-outline fs-4 text-muted"></i>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-1 fw-bold">2FA Authentication</h6>
                                                        {twoFaStatus === 1 ? (
                                                            <p className="text-success mb-0 small">
                                                                <i className="mdi mdi-check-circle me-1" />
                                                                Two-factor authentication is currently enabled on your account.
                                                            </p>
                                                        ) : (
                                                            <p className="text-muted mb-0 small">Enhance your account security by enabling two-factor authentication.</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    {twoFaStatus === 1 ? (
                                                        <button
                                                            className="btn btn-danger px-4"
                                                            data-bs-toggle="modal"
                                                            data-bs-target="#disable2FaModal"
                                                            disabled={twoFaLoading}
                                                        >
                                                            Disable 2FA
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="btn btn-primary px-4"
                                                            onClick={handle2FASetup}
                                                            disabled={twoFaLoading}
                                                        >
                                                            {twoFaLoading ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                                                    Loading...
                                                                </>
                                                            ) : (
                                                                "Setup 2FA"
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Change Password Block */}
                                    <div className="card mb-3">
                                        <div className="card-header bg-transparent border-bottom-0 pb-0 pt-4">
                                            <h5 className="mb-0 fw-bold">Advanced Security :</h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="bg-light rounded p-3 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                                        <i className="mdi mdi-lock-outline fs-4 text-muted"></i>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-1 fw-bold">Login Password</h6>
                                                        <p className="text-muted mb-0 small">Increase your password strength to enhance account security.</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <button
                                                        className="btn btn-primary px-4"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#changePasswordModal"
                                                    >
                                                        Change
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Device Management Block */}
                                    <div className="card mb-3">
                                        <div className="card-header bg-transparent border-bottom-0 pb-0 pt-4">
                                            <h5 className="mb-0 fw-bold">Device Management :</h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="bg-light rounded p-3 d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                                        <i className="mdi mdi-cellphone-link fs-4 text-muted"></i>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-1 fw-bold">Active Devices</h6>
                                                        <p className="text-muted mb-0 small">Monitor active sessions and terminate unrecognized devices.</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <button
                                                        className="btn btn-primary px-4"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#deviceManagementModal"
                                                    >
                                                        Manage
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>

            {createPortal(
                <>
                    <TwoFactorModal qrData={twoFaData} loading={twoFaLoading} onSuccess={getProfileData} loginId={loginId || userData?.loginid} />

                    <div className="modal fade" id="disable2FaModal" tabIndex="-1" aria-labelledby="disable2FaModalLabel" aria-hidden="true">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg">
                                <div className="modal-header bg-danger text-white">
                                    <h5 className="modal-title text-white" id="disable2FaModalLabel">Disable 2FA</h5>
                                    <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close" />
                                </div>
                                <div className="modal-body p-4 pt-2 text-center">
                                    <i className="mdi mdi-alert-circle text-danger" style={{ fontSize: "64px" }}></i>
                                    <h5 className="mb-3 fw-bold">Are you sure?</h5>
                                    <p className="text-muted">
                                        Disabling Two-Factor Authentication will significantly reduce your account security.
                                        Are you sure you want to proceed?
                                    </p>
                                    <div className="mb-3 text-center">
                                        <label className="form-label text-start d-block mb-3">Authenticator Code</label>
                                        <div
                                            className="d-flex justify-content-center gap-2 mb-3"
                                            onPaste={handleDisableOtpPaste}
                                        >
                                            {disableOtp.map((digit, idx) => (
                                                <input
                                                    key={idx}
                                                    ref={(el) => (disableOtpRefs.current[idx] = el)}
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={2}
                                                    value={digit}
                                                    onFocus={(e) => e.target.select()}
                                                    onChange={(e) => handleDisableOtpInput(e, idx)}
                                                    onKeyDown={(e) => handleDisableOtpKeyDown(e, idx)}
                                                    autoComplete="one-time-code"
                                                    className={`form-control text-center fw-bold p-0 otp-input-box${digit ? " border-primary" : ""}`}
                                                    style={{
                                                        boxShadow: digit ? "0 0 0 0.15rem rgba(var(--bs-primary-rgb), 0.2)" : "none",
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-center gap-3 mt-4 pt-2">
                                        <button type="button" className="btn btn-light px-4" data-bs-dismiss="modal" disabled={twoFaLoading}>
                                            Cancel
                                        </button>
                                        <style>{`
                                            .modal {
                                                z-index: 1100 !important;
                                            }
                                            .otp-input-box {
                                                width: 50px;
                                                height: 55px;
                                                font-size: 20px;
                                                border-radius: 8px;
                                                border-width: 1px;
                                                transition: border-color 0.2s, box-shadow 0.2s;
                                            }
                                            @media (max-width: 420px) {
                                                .otp-input-box {
                                                    width: 40px !important;
                                                    height: 48px !important;
                                                    font-size: 18px !important;
                                                }
                                            }
                                            @media (max-width: 340px) {
                                                .otp-input-box {
                                                    width: 35px !important;
                                                    height: 42px !important;
                                                    font-size: 16px !important;
                                                }
                                            }
                                        `}</style>
                                        <button type="button" className="btn btn-danger px-4" onClick={confirmDisable2FA} disabled={twoFaLoading}>
                                            {twoFaLoading ? (
                                                <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Disabling...</>
                                            ) : "Yes, Disable 2FA"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal fade" id="changePasswordModal" tabIndex="-1" aria-hidden="true">
                        <div className="modal-dialog modal-dialog-centered modal-md">
                            <div className="modal-content">
                                <div className="modal-header border-bottom-0 pb-0">
                                    <h5 className="modal-title fw-bold">Change Password</h5>
                                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                                </div>
                                <div className="modal-body p-0">
                                    <ChangePassword isModal={true} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal fade" id="deviceManagementModal" tabIndex="-1" aria-hidden="true">
                        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: "620px" }}>
                            <div className="modal-content shadow-lg border-0" style={{ maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
                                <div className="modal-header border-bottom px-4 py-3 bg-white sticky-top">
                                    <h5 className="modal-title fw-bold mb-0">Device Management</h5>
                                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                                </div>
                                <div className="modal-body px-4 py-3" style={{ overflowY: "auto", overflowX: "hidden" }}>
                                    <DeviceManagement isModal={true} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>,
                document.getElementById("root") || document.body
            )}
        </>
    );
}
